import { captureException } from "@sentry/react";
import { ASSOCIATED_TOKEN_PROGRAM_ID, getAssociatedTokenAddress } from "@solana/spl-token0.3.8";
import { useConnection } from "@solana/wallet-adapter-react";
import { PublicKey } from "@solana/web3.js";
import { SWAP_HOST_FEE_ADDRESS } from "@stepfinance/common";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";
import { SPLTokenFetcher } from "@stepfinance/tokens/src/lib/positions/solana";
import { useCallback, useEffect, useState } from "react";

import { YIELD_BEARING_MINTS } from "../../components/dashboard/Balances/YieldBearingAssets";
import { DUST_THRESHOLD, useHideDustConfig, useUnknownTokenConfig } from "../../contexts/app/config";
import { useEnrichedWallet } from "../../contexts/enrichedWallet";
import { useLendingContext } from "../../contexts/lending";
import { useMarkets } from "../../contexts/market";
import { useRefreshUpdateContext } from "../../contexts/refresh";
import { updateNativeAccount } from "../../utils/accounts/native";
import { cache, precacheUserTokenAccounts } from "../../utils/cache";
import { getTokenByMint, isKnownMint } from "../../utils/tokenInfo";
import { fetchAndCacheMetaplexMetadata } from "../../utils/utils";

export interface ParsedUserTokenAccounts {
  yieldBearingAssetsBalanceUSD: number | null;
  spotAssetsBalanceUSD: number | null;
  userTokenAccountsBalanceUSD: number | null;
  yieldBearingTokenAccounts: NormalizedTokenAccount[];
  spotAssetTokenAccounts: NormalizedTokenAccount[];
  zeroBalanceTokenAccounts: NormalizedTokenAccount[];
  zeroDecimalTokenAccounts: NormalizedTokenAccount[];
}

export type UserTokenAccountsMap = {
  [mint: string]: number; // index of userTokenAccounts
};

export const initialParsedUserTokenAccounts = {
  yieldBearingAssetsBalanceUSD: null,
  spotAssetsBalanceUSD: null,
  userTokenAccountsBalanceUSD: null,
  yieldBearingTokenAccounts: [],
  spotAssetTokenAccounts: [],
  zeroBalanceTokenAccounts: [],
  zeroDecimalTokenAccounts: [],
};

export const useTokenAccounts = () => {
  const [userTokenAccounts, setUserTokenAccounts] = useState<NormalizedTokenAccount[]>([]);
  const [userTokenAccountsMap, setUserTokenAccountsMap] = useState<UserTokenAccountsMap>({});
  const [fetchedMetaplexMintlist, setFetchedMetaplexMintlist] = useState<string[]>([]);
  const [parsedUserTokenAccounts, setParsedUserTokenAccounts] =
    useState<ParsedUserTokenAccounts>(initialParsedUserTokenAccounts);
  const { publicKey, connected } = useEnrichedWallet();
  const { connection } = useConnection();
  const { priceInUSD, marketsInitialised } = useMarkets();
  const { hideUnknownTokens } = useUnknownTokenConfig();
  const { hideDust } = useHideDustConfig();
  const refreshContexts = useRefreshUpdateContext();
  const {
    setUserTokenAccountsForTulip,
    platforms: {
      solend: { solendCTokenMints },
    },
  } = useLendingContext();

  // FIXME: to be fixed in SF-1531 https://step-finance.atlassian.net/browse/SF-1531
  useEffect(() => {
    if (userTokenAccounts.length > 0) {
      setUserTokenAccountsForTulip(userTokenAccounts);
    } else {
      setUserTokenAccountsForTulip([]);
    }
  }, [userTokenAccounts, setUserTokenAccountsForTulip]);

  useEffect(() => {
    setUserTokenAccounts([]);
    setParsedUserTokenAccounts(initialParsedUserTokenAccounts);
  }, [publicKey, connected]);

  const updateFilters = useCallback((userTokenAccounts: NormalizedTokenAccount[], publicKey) => {
    // decoupled fetch to set ATA filters since this is
    // only needed on the accounts page. allows quicker render
    // of dashboard.
    const filterFetchList: Promise<NormalizedTokenAccount>[] = [];
    for (let i = 0; userTokenAccounts.length > 0 && i < userTokenAccounts.length; i++) {
      const updateFilters = async () => {
        const tokenAccount = userTokenAccounts[i];
        const associatedAddress = await getAssociatedTokenAddress(
          new PublicKey(tokenAccount.info.mint),
          publicKey,
          true,
          new PublicKey(tokenAccount.programId),
          ASSOCIATED_TOKEN_PROGRAM_ID,
        );
        const filters = { ...tokenAccount.filters };
        return {
          ...tokenAccount,
          filters: {
            safe: associatedAddress.toBase58() === tokenAccount.pubkey && filters.safe,
            unknown: filters.unknown,
            nonAta: !(associatedAddress.toBase58() === tokenAccount.pubkey),
          },
        };
      };
      filterFetchList.push(updateFilters());
    }
    if (filterFetchList.length > 0) {
      Promise.all(filterFetchList).then(setUserTokenAccounts).catch(captureException);
    }
  }, []);

  const fetchTokenAccounts = useCallback(async () => {
    if (publicKey && marketsInitialised && connected) {
      // populate cache with Step treasury owned token accounts
      void precacheUserTokenAccounts(connection, SWAP_HOST_FEE_ADDRESS);
      //  populate cache with user native (SOL) token account.
      void precacheUserTokenAccounts(connection, publicKey);
      // populate native account to cache for TX history parsing
      void updateNativeAccount(connection, cache.getAll(), cache.getAllMints(), publicKey);

      const splTokenFetcher = new SPLTokenFetcher(publicKey, connection, getTokenByMint, priceInUSD);
      const splTokenFetchResults = await splTokenFetcher.fetchPositions();

      const mintsToFetch = splTokenFetchResults.mintsNotFound.filter((mint) => !fetchedMetaplexMintlist.includes(mint));
      if (mintsToFetch.length > 0) {
        const mintPubkeys = mintsToFetch.map((mint) => new PublicKey(mint));
        await fetchAndCacheMetaplexMetadata(connection, mintPubkeys, true);

        setFetchedMetaplexMintlist((current) => [...current, ...mintsToFetch]);
        void refreshContexts();
      }

      setUserTokenAccounts(splTokenFetchResults.positions);
      setUserTokenAccountsMap(splTokenFetchResults.tokenAccountsMap);
      updateFilters(splTokenFetchResults.positions, publicKey);
    }
  }, [
    connected,
    connection,
    fetchedMetaplexMintlist,
    marketsInitialised,
    priceInUSD,
    publicKey,
    updateFilters,
    refreshContexts,
  ]);

  useEffect(() => {
    void fetchTokenAccounts();
  }, [fetchTokenAccounts]);

  const parseUserTokenAccounts = useCallback(() => {
    const spotAssetTokenAccountsMap: { [key: string]: NormalizedTokenAccount } = {};
    const yieldBearingTokenAccounts: NormalizedTokenAccount[] = [];
    const zeroBalanceTokenAccounts: NormalizedTokenAccount[] = [];
    const zeroDecimalTokenAccounts: NormalizedTokenAccount[] = [];
    let yieldBearingAssetsBalanceUSD: number | null = null;
    let spotAssetsBalanceUSD: number | null = null;

    for (let i = 0; userTokenAccounts.length > 0 && i < userTokenAccounts.length; i++) {
      const tokenAccount = userTokenAccounts[i];
      const {
        info: { mint },
        tokenAmount: { uiAmount, balanceInUSD },
      } = tokenAccount;
      if (uiAmount === 0) {
        zeroBalanceTokenAccounts.push(tokenAccount);
      }
      if (uiAmount > 0 && tokenAccount.tokenAmount.decimals === 0) {
        zeroDecimalTokenAccounts.push(tokenAccount);
      }
      const isYieldBearingAsset = YIELD_BEARING_MINTS.has(mint) || solendCTokenMints.includes(mint);
      const tokenInfo = getTokenByMint(mint);
      const isNFT = tokenInfo?.tags?.includes("nft");
      if ((hideUnknownTokens && !isKnownMint(mint, hideUnknownTokens)) || !tokenInfo || isNFT) {
        continue;
      }
      if (hideDust && (balanceInUSD ?? 0) < DUST_THRESHOLD) {
        continue;
      }

      if (isYieldBearingAsset && balanceInUSD && uiAmount > 0) {
        yieldBearingTokenAccounts.push(tokenAccount);
        yieldBearingAssetsBalanceUSD =
          balanceInUSD !== null ? (yieldBearingAssetsBalanceUSD ?? 0) + balanceInUSD : yieldBearingAssetsBalanceUSD;
      } else if (uiAmount > 0) {
        spotAssetTokenAccountsMap[mint] = tokenAccount;
        spotAssetsBalanceUSD =
          balanceInUSD !== null ? (spotAssetsBalanceUSD ?? 0) + balanceInUSD : spotAssetsBalanceUSD;
      }
    }

    const spotAssetTokenAccounts = Object.values(spotAssetTokenAccountsMap);

    let userTokenAccountsBalanceUSD: number | null = null;
    if (yieldBearingAssetsBalanceUSD !== null) {
      userTokenAccountsBalanceUSD = yieldBearingAssetsBalanceUSD;
    }
    if (spotAssetsBalanceUSD !== null) {
      userTokenAccountsBalanceUSD = (userTokenAccountsBalanceUSD ?? 0) + spotAssetsBalanceUSD;
    }

    setParsedUserTokenAccounts({
      yieldBearingAssetsBalanceUSD,
      spotAssetsBalanceUSD,
      userTokenAccountsBalanceUSD,
      yieldBearingTokenAccounts,
      spotAssetTokenAccounts,
      zeroBalanceTokenAccounts,
      zeroDecimalTokenAccounts,
    });
  }, [hideDust, hideUnknownTokens, solendCTokenMints, userTokenAccounts]);

  useEffect(() => {
    void parseUserTokenAccounts();
  }, [parseUserTokenAccounts, fetchedMetaplexMintlist]);

  return { userTokenAccounts, userTokenAccountsMap, parsedUserTokenAccounts, fetchTokenAccounts };
};
